import { useEffect, useState } from 'react';
import { crm } from '@/lib/crm-client';
import { ConfirmDialog } from '@/components/ConfirmDialog';

/**
 * Löschen mit Rückfrage.
 *
 * Die Rückfrage nennt konkret, was am Kontakt hängt — eine allgemeine
 * „Wirklich löschen?“-Frage lässt niemanden erkennen, ob er gerade drei
 * Jahre Schriftverkehr anfasst.
 */
export function DeleteContactDialog({
  contact,
  onClose,
  onDeleted,
}: {
  contact: { id: string; displayName: string };
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [impact, setImpact] = useState<{
    emails: number;
    deals: number;
    tasks: number;
    drafts: number;
    lists: number;
    campaigns: number;
  } | null>(null);

  useEffect(() => {
    void crm.contacts.deletionImpact(contact.id).then(setImpact);
  }, [contact.id]);

  const teile: string[] = [];
  if (impact?.emails) teile.push(`${impact.emails} E-Mail${impact.emails === 1 ? '' : 's'}`);
  if (impact?.deals) teile.push(`${impact.deals} Deal${impact.deals === 1 ? '' : 's'}`);
  if (impact?.tasks) teile.push(`${impact.tasks} Aufgabe${impact.tasks === 1 ? '' : 'n'}`);
  if (impact?.drafts)
    teile.push(`${impact.drafts} Entwurf${impact.drafts === 1 ? '' : 'e'}`);
  if (impact?.lists) teile.push(`${impact.lists} Lead-Liste${impact.lists === 1 ? '' : 'n'}`);
  if (impact?.campaigns)
    teile.push(`${impact.campaigns} Kampagne${impact.campaigns === 1 ? '' : 'n'}`);

  return (
    <ConfirmDialog
      title={`${contact.displayName} löschen?`}
      confirmLabel="Löschen"
      destructive
      onCancel={onClose}
      onConfirm={async () => {
        await crm.contacts.delete(contact.id);
        onDeleted();
      }}
    >
      {impact === null ? (
        <p className="text-sm text-muted-foreground">Prüfe, was daran hängt …</p>
      ) : teile.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          An diesem Kontakt hängt nichts weiter.
        </p>
      ) : (
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            Daran hängen <strong className="text-foreground">{teile.join(', ')}</strong>.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            {impact.emails > 0 && (
              <li>
                Der Schriftverkehr bleibt erhalten und wandert nach „Nicht zugeordnet“.
              </li>
            )}
            {impact.deals > 0 && (
              <li>Deals bleiben bestehen, verlieren aber den Ansprechpartner.</li>
            )}
            {impact.tasks > 0 && <li>Die Aufgaben werden mitgelöscht.</li>}
            {impact.drafts > 0 && (
              <li>Vorbereitete, nicht gesendete Entwürfe werden mitgelöscht.</li>
            )}
            {(impact.lists > 0 || impact.campaigns > 0) && (
              <li>
                Er wird aus Listen und Kampagnen entfernt — die Listen und Kampagnen
                selbst bleiben.
              </li>
            )}
          </ul>
        </div>
      )}
    </ConfirmDialog>
  );
}
